import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, lazy, Suspense, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";

// Το sonner (~8,5 KiB) δεν χρειάζεται στο πρώτο paint: φορτώνεται lazy μετά το
// hydration, ώστε να μη μετράει ως αχρησιμοποίητο JavaScript στην αρχική φόρτωση.
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
import { PageLoader } from "@/components/storefront/PageLoader";
import { hasStoredSession } from "@/lib/has-stored-session";
import { CookieConsentBanner } from "@/components/storefront/cookie-consent";
import { DeferredMount } from "@/components/storefront/DeferredMount";
import { SessionGatedMount } from "@/components/storefront/SessionGatedMount";
import { ComingSoon } from "@/components/storefront/ComingSoon";
import { OfflineBanner } from "@/components/storefront/OfflineBanner";
import { registerPwa } from "@/lib/pwa";
import { getMaintenanceStatus } from "@/lib/maintenance.functions";
import appCss from "../styles.css?inline";
import ogShare from "@/assets/og-share.png.asset.json";
import interGreek from "@fontsource-variable/inter/files/inter-greek-wght-normal.woff2?url";
import { installStaleReload } from "@/lib/stale-reload";

// Non-critical, client-only widgets. Loaded as separate chunks and mounted
// only after the browser is idle (see DeferredMount) so they don't add to the
// initial JS parse/execute cost on first load.
const GoogleAnalytics = lazy(() =>
  import("@/components/storefront/GoogleAnalytics").then((m) => ({ default: m.GoogleAnalytics })),
);
const ExitIntentPopup = lazy(() =>
  import("@/components/storefront/ExitIntentPopup").then((m) => ({ default: m.ExitIntentPopup })),
);
const ScrollToTopButton = lazy(() =>
  import("@/components/storefront/ScrollToTopButton").then((m) => ({ default: m.ScrollToTopButton })),
);
const SnowfallOverlay = lazy(() =>
  import("@/components/storefront/SnowfallOverlay").then((m) => ({ default: m.SnowfallOverlay })),
);
// Banner καλωσορίσματος που προωθεί το Pandaki (μία φορά ανά συνεδρία).
const PandakiPromo = lazy(() =>
  import("@/components/storefront/PandakiPromo").then((m) => ({ default: m.PandakiPromo })),
);

const SupportChatLauncher = lazy(() =>
  import("@/components/storefront/SupportChatLauncher").then((m) => ({
    default: m.SupportChatLauncher,
  })),
);
const PandaOfferPeek = lazy(() =>
  import("@/components/storefront/PandaOfferPeek").then((m) => ({ default: m.PandaOfferPeek })),
);
const PandaGameModal = lazy(() =>
  import("@/components/storefront/PandaGameModal").then((m) => ({ default: m.PandaGameModal })),
);
// CartSync/WishlistSync statically import their server functions, which pull
// the auth middleware (and through it the full @supabase/supabase-js client)
// into whatever chunk imports them. Loading them lazily keeps that ~53 KiB
// supabase chunk out of the main entry bundle and off the homepage's
// modulepreload list — it's fetched after the browser is idle instead.
const CartSync = lazy(() =>
  import("@/components/storefront/CartSync").then((m) => ({ default: m.CartSync })),
);
const WishlistSync = lazy(() =>
  import("@/components/storefront/WishlistSync").then((m) => ({ default: m.WishlistSync })),
);

const OG_IMAGE = `https://mrpanda.gr${ogShare.url}`;
// Τρέχει πριν από κάθε JS: αν ο σύνδεσμος επαναφοράς προσγειωθεί σε
// οποιαδήποτε σελίδα, σημειώνουμε τη ροή (πριν η βιβλιοθήκη σύνδεσης σβήσει το
// #access_token από τη διεύθυνση) και πηγαίνουμε στη σελίδα νέου κωδικού.
const RECOVERY_REDIRECT_SCRIPT =
  "(function(){try{var path=window.location.pathname.replace(/\\/$/,'')||'/';var hash=window.location.hash||'';var search=window.location.search||'';var combined=hash+'&'+search;var isRec=/(^|[#&?])type=recovery\\b/.test(combined)||(/(^|[#&?])access_token=/.test(hash)&&/recovery/.test(combined));var isErr=/(^|[#&?])error_code=otp_expired\\b/.test(combined)||(/(^|[#&?])error=access_denied\\b/.test(hash));if(!isRec){if(isErr&&path==='/reset-password'){try{sessionStorage.setItem('mp-recovery','error');}catch(e){}}return;}try{sessionStorage.setItem('mp-recovery','1');}catch(e){}if(path!=='/reset-password'){window.location.replace('/reset-password'+search+hash);}}catch(e){}})();";

function NotFoundComponent() {
  return (
    <div className="xmas-bg relative flex min-h-[80vh] items-center justify-center overflow-hidden bg-background px-4">

      {/* Floating decorative bamboo leaves */}
      <span className="absolute top-12 left-8 text-3xl opacity-20 animate-float-y" style={{ animationDelay: "0s" }}>🎋</span>
      <span className="absolute top-24 right-12 text-2xl opacity-15 animate-float-y" style={{ animationDelay: "0.8s" }}>🎋</span>
      <span className="absolute bottom-20 left-16 text-2xl opacity-15 animate-float-y" style={{ animationDelay: "1.4s" }}>🍃</span>
      <span className="absolute bottom-32 right-8 text-3xl opacity-20 animate-float-y" style={{ animationDelay: "2s" }}>🍃</span>
      <span className="absolute top-1/3 left-4 text-xl opacity-10 animate-float-y" style={{ animationDelay: "0.5s" }}>🎋</span>

      <div className="relative z-10 max-w-lg text-center animate-pop-in">
        {/* Playful panda peeking */}
        <div className="relative mx-auto mb-6 inline-block">
          <div className="text-[6rem] leading-none animate-bob">🐼</div>
          <span className="absolute -top-1 -right-4 text-2xl animate-float-y" style={{ animationDelay: "0.3s" }}>❓</span>
        </div>

        <h1 className="text-6xl font-extrabold tracking-tight text-foreground">Ουπς!</h1>
        <p className="mt-2 text-2xl font-semibold text-primary">Η σελίδα χάθηκε στο δάσος 🎋</p>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Ο MrPanda την έψαξε παντού, αλλά δεν τη βρήκε!
          <br />
          Μήπως μετακινήθηκε ή πληκτρολόγησες κάτι λάθος;
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="mp-bouncy inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-primary/30"
          >
            🏠 Πίσω στο σπίτι
          </Link>
          <button
            onClick={() => window.history.back()}
            className="mp-bouncy inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-base font-medium text-foreground shadow-sm hover:bg-accent"
          >
            ⬅️ Πήγαινε πίσω
          </button>
        </div>

        {/* Playful 404 code */}
        <div className="mt-10 inline-flex items-center gap-2 rounded-2xl bg-muted/60 px-5 py-2.5 text-sm font-mono text-muted-foreground">
          <span>Κωδικός λάθους:</span>
          <span className="font-bold text-foreground">404</span>
          <span className="text-xs">— Η σελίδα δεν βρέθηκε 🐾</span>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error: rawError, reset }: { error: unknown; reset: () => void }) {
  const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  console.error(error);

  return (
    <div className="relative flex min-h-[80vh] items-center justify-center overflow-hidden bg-background px-4">
      {/* Floating decorative bamboo leaves */}
      <span className="absolute top-12 left-8 text-3xl opacity-20 animate-float-y" style={{ animationDelay: "0s" }}>🎋</span>
      <span className="absolute top-24 right-12 text-2xl opacity-15 animate-float-y" style={{ animationDelay: "0.8s" }}>🎋</span>
      <span className="absolute bottom-20 left-16 text-2xl opacity-15 animate-float-y" style={{ animationDelay: "1.4s" }}>🍃</span>
      <span className="absolute bottom-32 right-8 text-3xl opacity-20 animate-float-y" style={{ animationDelay: "2s" }}>🍃</span>
      <span className="absolute top-1/3 left-4 text-xl opacity-10 animate-float-y" style={{ animationDelay: "0.5s" }}>🎋</span>

      <div className="relative z-10 max-w-lg text-center animate-pop-in">
        {/* Playful dizzy panda */}
        <div className="relative mx-auto mb-6 inline-block">
          <div className="text-[6rem] leading-none animate-bob">🐼</div>
          <span className="absolute -top-1 -right-4 text-2xl animate-float-y" style={{ animationDelay: "0.3s" }}>💫</span>
        </div>

        <h1 className="text-6xl font-extrabold tracking-tight text-foreground">Ωχ!</h1>
        <p className="mt-2 text-2xl font-semibold text-primary">Κάτι πήγε στραβά 🎋</p>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Ο MrPanda προσπάθησε πολύ, αλλά η σελίδα δεν φόρτωσε.
          <br />
          Δοκίμασε να την ανανεώσεις ή γύρνα στην αρχική!
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => {
              reset();
              window.location.reload();
            }}
            className="mp-bouncy inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-primary/30"
          >
            🔄 Δοκίμασε ξανά
          </button>
          <a
            href="/"
            className="mp-bouncy inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-base font-medium text-foreground shadow-sm hover:bg-accent"
          >
            🏠 Πίσω στο σπίτι
          </a>
        </div>

        {/* Playful error code */}
        <div className="mt-10 inline-flex items-center gap-2 rounded-2xl bg-muted/60 px-5 py-2.5 text-sm font-mono text-muted-foreground">
          <span>Κωδικός λάθους:</span>
          <span className="font-bold text-foreground">500</span>
          <span className="text-xs">— Σφάλμα συστήματος 🐾</span>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Ο έλεγχος συντήρησης τρέχει στο SSR. Στον browser ΔΕΝ τον ξανακαλούμε σε
  // κάθε πλοήγηση (staleTime: Infinity / shouldReload: false) — έτσι δεν
  // δημιουργούνται συνεχή αιτήματα `/_serverFn/...` που, όταν κοπεί στιγμιαία
  // το δίκτυο, καταγράφονται ως ERR_CONNECTION_FAILED στην κονσόλα.
  // Και σε αποτυχία επιστρέφουμε ασφαλή προεπιλογή αντί για σφάλμα.
  loader: async () => {
    try {
      return await getMaintenanceStatus();
    } catch {
      return { active: false, message: null };
    }
  },
  staleTime: Infinity,
  shouldReload: false,

  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "DXF48jPddKDZkUmu-Ghd0vjOPo_oVcQrmea8xYD3Wjc" },
      { title: "MrPanda.gr — Χιλιάδες προϊόντα στις καλύτερες τιμές" },
      { name: "description", content: "Αγόρασε εύκολα από το MrPanda.gr με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα." },
      { name: "author", content: "MrPanda.gr" },
      { name: "theme-color", content: "#c75100" },
      { property: "og:title", content: "MrPanda.gr — Χιλιάδες προϊόντα στις καλύτερες τιμές" },
      { property: "og:description", content: "Αγόρασε εύκολα από το MrPanda.gr με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "MrPanda.gr" },
      { property: "og:locale", content: "el_GR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "MrPanda.gr — Χιλιάδες προϊόντα στις καλύτερες τιμές" },
      { name: "twitter:description", content: "Αγόρασε εύκολα από το MrPanda.gr με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα." },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "MrPanda.gr — Χιλιάδες προϊόντα στις καλύτερες τιμές" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      {
        rel: "manifest",
        href: "/manifest.webmanifest",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        // LCP: μόνο το ελληνικό subset προφορτώνεται. Το λατινικό ανταγωνιζόταν
        // την hero εικόνα για εύρος ζώνης στα πρώτα ms και μεγάλωνε την
        // «καθυστέρηση φόρτωσης πόρου» του LCP· φορτώνεται κανονικά από το CSS.
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interGreek,
        crossOrigin: "anonymous",
      },
      {
        rel: "preconnect",
        href: "https://btpkfobaqmykxxanwrbp.supabase.co",
        crossOrigin: "anonymous",
      },

    ],
    scripts: [
      {
        // Αν το HTML φτάσει στον browser χωρίς τα dehydrated δεδομένα του router
        // (π.χ. σελίδα από cache/proxy ή διακοπή του SSR stream), το hydration
        // πετάει invariant και μένει λευκή οθόνη. Το πιάνουμε πριν από όλα και
        // κάνουμε ΜΙΑ φορά καθαρό reload (cache-bust) για να έρθει σωστό HTML.
        children:
          "(function(){function r(m){if(!m||m.indexOf('$_TSR')<0)return;try{if(sessionStorage.getItem('mp-hydrate-retry'))return;sessionStorage.setItem('mp-hydrate-retry','1');}catch(e){}var u=new URL(location.href);u.searchParams.set('_r',String(Date.now()));location.replace(u.toString());}window.addEventListener('error',function(e){r(e&&e.message);});window.addEventListener('unhandledrejection',function(e){var x=e&&e.reason;r(x&&(x.message||String(x)));});window.addEventListener('load',function(){setTimeout(function(){try{sessionStorage.removeItem('mp-hydrate-retry');}catch(e){}},5000);});})();",
      },
      {
        // Old recovery emails may redirect to the home page. Move them to the
        // password-reset route before the auth client consumes the URL hash.
        children: RECOVERY_REDIRECT_SCRIPT,
      },

      {
        // Apply the saved theme before first paint to avoid a light/dark flash.
        // Default is light: only opt in to dark when explicitly stored.
        children:
          "(function(){try{if(localStorage.getItem('mp-theme')==='dark'){document.documentElement.classList.add('dark');}}catch(e){}})();",
      },
      {
        // Η μπάρα ανακοίνωσης αποδίδεται στον server ώστε να μη μετατοπίζεται
        // η σελίδα μετά το hydration (CLS). Αν ο χρήστης την έχει κλείσει, την
        // κρύβουμε πριν την πρώτη ζωγραφική με κλάση στο <html>.
        children:
          "(function(){try{var d=document.documentElement;var closed=false;try{closed=localStorage.getItem('mp-announce-closed-2026-09-18')==='1';}catch(e){}if(closed){d.classList.add('mp-announce-hidden');}else if(Date.now()<=Date.parse('2026-09-18T23:59:59+03:00')){d.classList.add('has-announce');}}catch(e){}})();",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MrPanda.gr",
          url: "https://mrpanda.gr",
          logo: OG_IMAGE,
          sameAs: [
            "https://www.instagram.com/mrpandagr/",
            "https://www.facebook.com/mrpandagr/",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "MrPanda.gr",
          url: "https://mrpanda.gr",
          inLanguage: "el",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://mrpanda.gr/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Store",
          "@id": "https://mrpanda.gr/#store",
          name: "MrPanda.gr",
          description:
            "Το MrPanda.gr είναι ελληνικό ηλεκτρονικό κατάστημα με είδη σπιτιού, καθαριστικά, τρόφιμα, χαρτικά και σχολικά, με χαμηλές τιμές και γρήγορη παράδοση σε όλη την Ελλάδα.",
          image: OG_IMAGE,
          logo: OG_IMAGE,
          url: "https://mrpanda.gr",
          telephone: "+302311822500",
          email: "info@mrpanda.gr",
          priceRange: "€€",
          currenciesAccepted: "EUR",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Π. Λεβαντή 15Α",
            addressLocality: "Σταυρούπολη Θεσσαλονίκης",
            addressRegion: "Κεντρική Μακεδονία",
            postalCode: "564 29",
            addressCountry: "GR",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 40.6635625,
            longitude: 22.9400625,
          },
          hasMap:
            "https://maps.google.com/maps?q=40.6635625,22.9400625",
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Wednesday", "Friday"],
              opens: "10:00",
              closes: "16:00",
            },
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Wednesday", "Friday"],
              opens: "18:00",
              closes: "20:00",
            },
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Tuesday", "Thursday"],
              opens: "10:00",
              closes: "16:00",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Κύρια πλοήγηση MrPanda.gr",
          itemListElement: [
            { "@type": "SiteNavigationElement", position: 1, name: "Προσφορές", url: "https://mrpanda.gr/offers" },
            { "@type": "SiteNavigationElement", position: 2, name: "Νέες αφίξεις", url: "https://mrpanda.gr/new-products" },
            { "@type": "SiteNavigationElement", position: 3, name: "Κορυφαία σε πωλήσεις", url: "https://mrpanda.gr/best-sellers" },
            { "@type": "SiteNavigationElement", position: 4, name: "Σχετικά με εμάς", url: "https://mrpanda.gr/about" },
            { "@type": "SiteNavigationElement", position: 5, name: "Επικοινωνία", url: "https://mrpanda.gr/contact" },
            { "@type": "SiteNavigationElement", position: 6, name: "Τρόποι αποστολής", url: "https://mrpanda.gr/shipping-methods" },
            { "@type": "SiteNavigationElement", position: 7, name: "Τρόποι πληρωμής", url: "https://mrpanda.gr/payment-methods" },
            { "@type": "SiteNavigationElement", position: 8, name: "Επιστροφές", url: "https://mrpanda.gr/returns" },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="el" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Inline the (small) compiled CSS so it is not a render-blocking
         * network request on the critical path — improves first paint / LCP. */}
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const maintenance = Route.useLoaderData();

  useEffect(() => {
    installStaleReload();
    registerPwa();
  }, []);

  if (maintenance?.active) {
    return (
      <QueryClientProvider client={queryClient}>
        <ComingSoon message={maintenance.message} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      <PageLoader />
      <Outlet />

      {/* 🎅 Το έλκηθρο του Άη-Βασίλη διασχίζει την οθόνη — μόνο CSS, χωρίς JavaScript. */}
      <div aria-hidden className="xmas-santa-fly" />
      <DeferredMount>
        <Suspense fallback={null}>
          <SnowfallOverlay />
        </Suspense>
      </DeferredMount>

      <DeferredMount>
        <Suspense fallback={null}>
          <Toaster />
        </Suspense>
      </DeferredMount>
      <OfflineBanner />
      <DeferredMount>
        {/* The cookie banner is client-only and animates in (opacity 0→1) after
            hydration. Rendered eagerly it became the LCP element with a large
            "render delay" (it was the largest thing painted, but painted late).
            Deferring it past first paint keeps the server-rendered hero as the
            LCP element with ~0ms render delay; the banner still appears as soon
            as the browser is idle. */}
        <CookieConsentBanner />
      </DeferredMount>
      {/* Τα υπόλοιπα widgets μπαίνουν σε διαδοχικά κύματα ώστε να μη
          δημιουργείται μία μεγάλη εργασία στο κύριο νήμα. */}
      <DeferredMount phase={1}>
        <Suspense fallback={null}>
          <GoogleAnalytics />
          <ScrollToTopButton />
        </Suspense>
      </DeferredMount>
      <DeferredMount phase={2}>
        <Suspense fallback={null}>
          <HideOnPaths prefixes={["/pandaki"]}><SupportChatLauncher /></HideOnPaths>
          <ExitIntentPopup />
          <PandakiPromo />
        </Suspense>
      </DeferredMount>
      <DeferredMount phase={3}>
        <Suspense fallback={null}>
          <HideOnCheckout>
            <PandaOfferPeek />
          </HideOnCheckout>
        </Suspense>
        <SessionGatedMount>
          <Suspense fallback={null}>
            <CartSync />
            <WishlistSync />
            <HideOnCheckout><PandaGameModal /></HideOnCheckout>
          </Suspense>
        </SessionGatedMount>
      </DeferredMount>
    </QueryClientProvider>
  );
}

function AuthSync() {
  const router = useRouter();
  const queryClient = useQueryClient();
  useEffect(() => {
    let active = true;
    let subscription: { unsubscribe: () => void } | undefined;
    // Load the Supabase client lazily AND during browser idle time so its
    // ~53 KiB chunk stays out of the critical path entirely. It is fetched
    // only once the main thread is free (or on first user interaction),
    // keeping it out of the "unused JavaScript" measured at initial load.
    const start = () => {
      import("@/integrations/supabase/client").then(({ supabase }) => {
        if (!active) return;
        const { data } = supabase.auth.onAuthStateChange(() => {
          queryClient.invalidateQueries();
          router.invalidate();
        });
        subscription = data.subscription;
      });
    };

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    // Ανώνυμος επισκέπτης: δεν υπάρχει session να παρακολουθήσουμε, οπότε δεν
    // κατεβάζουμε καθόλου το chunk του Supabase στην αρχική φόρτωση. Το
    // φορτώνουμε μόνο μόλις ο χρήστης αλληλεπιδράσει με τη σελίδα.
    if (!hasStoredSession()) {
      const events = ["pointerdown", "keydown", "touchstart"] as const;
      const kick = () => {
        for (const ev of events) window.removeEventListener(ev, kick);
        start();
      };
      for (const ev of events) window.addEventListener(ev, kick, { once: true, passive: true });
      return () => {
        active = false;
        for (const ev of events) window.removeEventListener(ev, kick);
        subscription?.unsubscribe();
      };
    }
    const ric = (window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (typeof ric === "function") {
      idleId = ric(start, { timeout: 3000 });
    } else {
      timeoutId = setTimeout(start, 1200);
    }

    return () => {
      active = false;
      const cic = (window as typeof window & {
        cancelIdleCallback?: (id: number) => void;
      }).cancelIdleCallback;
      if (idleId !== undefined && typeof cic === "function") cic(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      subscription?.unsubscribe();
    };
  }, [router, queryClient]);
  return null;
}

function HideOnPaths({ prefixes, children }: { prefixes: string[]; children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (prefixes.some((p) => pathname.startsWith(p))) return null;
  return <>{children}</>;
}

// Στο ταμείο το πάντα με τις προσφορές μπορεί να κρύβει το κουμπί ολοκλήρωσης.
function HideOnCheckout({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname.startsWith("/checkout")) return null;
  return <>{children}</>;
}
